import { useContext, useEffect, useState } from "react";
import { HiX } from "react-icons/hi";
import { RxHamburgerMenu } from "react-icons/rx";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import {
  FaChevronDown,
  FaChevronUp,
  FaHandHoldingUsd,
  FaStoreAlt,
  FaUserTie,
  FaUsers,
  FaUser,
  FaLock,
  FaClock,
  FaHandshake,
  FaBuilding,
  FaBriefcase,
  FaStore,
} from "react-icons/fa";

import { Modal } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

import { AuthContext } from "../context";
import RoleInfoModal from "./roleModal";

const Header = () => {
  const {
    isAuthenticated,
    setIsAuthenticated,
    isAccountModalOpen,
    openAccountModal,
    closeAccountModal,
  } = useContext(AuthContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("user_website")
    ? JSON.parse(localStorage.getItem("user_website"))
    : localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  const isWalletUser = token?.userType === "member";

  /* =========================================================
     FETCH CATEGORIES
  ========================================================= */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategories([]);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  /* =========================================================
     BODY SCROLL
  ========================================================= */

  useEffect(() => {
    if (isFilterSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isFilterSidebarOpen]);

  /* =========================================================
     SIGNUP REDIRECTS
  ========================================================= */

  useEffect(() => {
    if (location.state?.openChannelPartnerSignUp) {
      const timer = setTimeout(() => {
        navigate("/signup/channelpartner", {
          state: {},
          replace: true,
        });
      }, 500);

      return () => clearTimeout(timer);
    }

    if (location.state?.openMemberSignUp) {
      const timer = setTimeout(() => {
        navigate("/signup/member", {
          state: {},
          replace: true,
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location.state, navigate]);

  /* =========================================================
     MENU
  ========================================================= */

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    setIsFilterSidebarOpen(false);
  };

  const toggleFilterSidebar = () => {
    setIsFilterSidebarOpen((prev) => !prev);
    setIsMenuOpen(false);
  };

  /* =========================================================
     CATEGORY
  ========================================================= */

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleSubcategorySelect = (category, subcategory) => {
    navigate(`/category/${category?._id}`, {
      state: {
        category,
        subcategory,
        products: subcategory.products,
      },
    });

    setIsFilterSidebarOpen(false);
  };

  /* =========================================================
     ACCOUNT
  ========================================================= */

  const handleAccountClick = () => {
    openAccountModal();
  };

  /* =========================================================
     ACCOUNT TYPE ACTION
  ========================================================= */

  const handleTypeClick = (type, action) => {
    setSelectedType(type);

    const basePath = action === "login" ? "/signin" : "/signup";

    if (type === "enduser") {
      navigate(`${basePath}/${type}`);

      if (action === "login") {
        closeAccountModal();
      }

      return;
    }

    if (action === "login") {
      if (type === "channelpartner") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/partnerlogin",
          "_blank"
        );
      } else if (type === "channelpartnerplus") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/partnerpluslogin",
          "_blank"
        );
      } else if (type === "corporate") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/corporatelogin",
          "_blank"
        );
      } else if (type === "director") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/directorlogin",
          "_blank"
        );
      } else if (type === "franchise") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/franchiselogin",
          "_blank"
        );
      } else if (type === "vendor") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/vendorlogin",
          "_blank"
        );
      } else if (type === "member") {
        navigate(`${basePath}/${type}`);
        closeAccountModal();
      }

      return;
    }

    if (action === "register") {
      navigate(`${basePath}/${type}`);
      closeAccountModal();
    }
  };

  /* =========================================================
     LOGOUT
  ========================================================= */

  const handleLogout = () => {
    localStorage.removeItem("user_website");

    if (isWalletUser) {
      localStorage.removeItem("user");
    }

    sessionStorage.clear();

    setIsAuthenticated(false);

    setIsMenuOpen(false);
    setIsFilterSidebarOpen(false);

    navigate("/", {
      state: {
        from: location.pathname,
      },
    });

    window.location.reload();
  };

  /* =========================================================
     NAVIGATION
  ========================================================= */

  const navigationItems = [
    {
      path: "/",
      label: "Home",
    },
    {
      path: "/shop",
      label: "Shop",
    },
    {
      path: "/services",
      label: "Services",
    },
    {
      path: "/AI-services",
      label: "AI Services",
    },
    {
      path: "/artery-vascular-age",
      label: "Artery-Vascular Age",
    },
    {
      path: "/antioxidant-measurement-test",
      label: "Antioxidant Measurement Test",
    },

     {
      path: "/blog",
      label: "Blogs",
    },
    {
      path: "/contact",
      label: "Contact Us",
    },
  ];

  /* =========================================================
     ACCOUNT TYPES
  ========================================================= */

  const accountTypes = [
    {
      title: "Member",
      icon: <FaUsers />,
      desc: "Shop products, track orders and enjoy member benefits.",
      type: "member",
      bg: "#e9f9ee",
      color: "#02BE0F",
      knowMore: true,
    },
    {
      title: "Channel Partner",
      icon: <FaHandshake />,
      desc: "For distributors and sales partners to grow business with us.",
      type: "channelpartner",
      bg: "#e7f1ff",
      color: "#0d6efd",
      knowMore: true,
    },
    {
      title: "Channel Partner Plus",
      icon: <FaUserTie />,
      desc: "Premium partners with higher commission and priority support.",
      type: "channelpartnerplus",
      bg: "#fff4e5",
      color: "#ff9800",
      knowMore: true,
    },
    {
      title: "Corporates & Industries & Professionals",
      icon: <FaBuilding />,
      desc: "For companies and professionals buying in bulk or services.",
      type: "corporate",
      bg: "#f3e8ff",
      color: "#6f42c1",
      knowMore: true,
    },
    {
      title: "Director / Super Distributor",
      icon: <FaBriefcase />,
      desc: "Manage distribution network and oversee partner operations.",
      type: "director",
      bg: "#f1f3f5",
      color: "#343a40",
      knowMore: true,
    },
    {
      title: "Vendor",
      icon: <FaStore />,
      desc: "Sell your products and manage inventory through our platform.",
      type: "vendor",
      bg: "#e6fffa",
      color: "#20c997",
      knowMore: true,
    },
    {
      title: "Franchise",
      icon: <FaStoreAlt />,
      desc: "For distributors and sales partners to grow business with us.",
      type: "franchise",
      bg: "#e7f1ff",
      color: "#0d6efd",
      knowMore: true,
    },
    {
      title: "End User",
      icon: <FaUser />,
      desc: "Purchase and use products/services directly from platform.",
      type: "enduser",
      bg: "#f8f9fa",
      color: "#6c757d",
      knowMore: true,
    },
    {
      title: "Investor",
      icon: <FaHandHoldingUsd />,
      desc: "Invest in LifeOnPlus and be part of India's digital healthcare growth.",
      type: "investor",
      bg: "#f0fdf4",
      color: "#15803d",
      knowMore: false,
    },
  ];

  const comingSoonTypes = [
    {
      label: "Affiliate",
      icon: <FaHandshake />,
    },
  ];

  return (
    <>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="lop-header">
        <div className="lop-header-inner">

          {/* LOGO */}

          <div className="lop-logo-area">
            <div className="lop-logo-wrapper">
              <img
                className="lop-logo"
                src="/lifeon-new.svg.jpeg"
                alt="LifeOnPlus Logo"
                onClick={() =>
                  navigate("/", {
                    state: {
                      from: location.pathname,
                    },
                  })
                }
              />
            </div>

            {/* MOBILE MENU BUTTON */}

            <button
              className="lop-mobile-button"
              onClick={toggleMenu}
              aria-label="Open menu"
            >
              <RxHamburgerMenu size={25} />
            </button>
          </div>

          {/* DESKTOP NAV */}

          <div className="lop-nav-wrapper">
            <nav className="lop-nav">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path;

                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`lop-nav-item ${
                      isActive ? "lop-nav-active" : ""
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* ACCOUNT AREA */}

          <div className="lop-account-area">
            {isAuthenticated ? (
              <>
                <a href="/cart" className="lop-action">
                  <span className="lop-action-icon">🛒</span>
                  <span>Cart</span>
                </a>

                <a href="/order-history" className="lop-action">
                  <span className="lop-action-icon">📋</span>
                  <span>Order</span>
                </a>

                {!isWalletUser && (
                  <a href="/profile" className="lop-action">
                    <span className="lop-action-icon">👤</span>
                    <span>Profile</span>
                  </a>
                )}

                <button
                  className="lop-logout"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <button
                className="lop-account-button"
                onClick={handleAccountClick}
              >
                Account
              </button>
            )}
          </div>
        </div>
      </header>

      {/* =====================================================
          FILTER OVERLAY
      ===================================================== */}

      <div
        className={`lop-filter-overlay ${
          isFilterSidebarOpen ? "lop-filter-overlay-show" : ""
        }`}
        onClick={toggleFilterSidebar}
      />

      {/* =====================================================
          FILTER SIDEBAR
      ===================================================== */}

      <div
        className={`lop-filter-sidebar ${
          isFilterSidebarOpen ? "lop-filter-sidebar-open" : ""
        }`}
      >
        <div className="lop-filter-header">
          <h2>Filters</h2>

          <button onClick={toggleFilterSidebar}>
            <HiX size={24} />
          </button>
        </div>

        <div className="lop-filter-content">
          {categories?.length > 0 ? (
            categories.map((category) => (
              <div
                key={category._id}
                className="lop-category"
              >
                <div
                  className="lop-category-title"
                  onClick={() =>
                    toggleCategory(category._id)
                  }
                >
                  <span>
                    {category.categoryName ||
                      "Unnamed Category"}
                  </span>

                  {expandedCategories[category._id] ? (
                    <FaChevronUp size={12} />
                  ) : (
                    <FaChevronDown size={12} />
                  )}
                </div>

                {expandedCategories[category._id] && (
                  <div className="lop-subcategories">
                    {category.subCategories?.map(
                      (subcategory) => (
                        <div
                          key={subcategory._id}
                          className="lop-subcategory"
                          onClick={() =>
                            handleSubcategorySelect(
                              category,
                              subcategory
                            )
                          }
                        >
                          {subcategory.subCategoryName ||
                            "Unnamed Subcategory"}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="lop-no-category">
              No categories available.
            </p>
          )}
        </div>
      </div>

      {/* =====================================================
          MOBILE MENU
      ===================================================== */}

      <div
        className={`lop-mobile-drawer ${
          isMenuOpen ? "lop-mobile-open" : ""
        }`}
      >
        <div className="lop-mobile-top">
          <span className="lop-mobile-title">
            LifeOnPlus
          </span>

          <button
            onClick={toggleMenu}
            className="lop-mobile-close"
          >
            <HiX size={25} />
          </button>
        </div>

        <div className="lop-mobile-content">
          {navigationItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setIsMenuOpen(false);
              }}
              className={`lop-mobile-nav ${
                location.pathname === item.path
                  ? "lop-mobile-active"
                  : ""
              }`}
            >
              {item.label}
            </button>
          ))}

          <div className="lop-mobile-divider" />

          {isAuthenticated ? (
            <>
              <a
                href="/cart"
                className="lop-mobile-action"
                onClick={() => setIsMenuOpen(false)}
              >
                🛒 &nbsp; Cart
              </a>

              <a
                href="/order-history"
                className="lop-mobile-action"
                onClick={() => setIsMenuOpen(false)}
              >
                📋 &nbsp; Orders
              </a>

              {!isWalletUser && (
                <a
                  href="/profile"
                  className="lop-mobile-action"
                  onClick={() => setIsMenuOpen(false)}
                >
                  👤 &nbsp; Profile
                </a>
              )}

              <button
                className="lop-mobile-logout"
                onClick={handleLogout}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={handleAccountClick}
              className="lop-mobile-account"
            >
              Account
            </button>
          )}
        </div>
      </div>

      {/* =====================================================
          ACCOUNT MODAL
      ===================================================== */}

      <Modal
        show={isAccountModalOpen}
        onHide={closeAccountModal}
        centered
        size="xl"
      >
        <Modal.Header
          closeButton
          className="lop-modal-header"
        >
          <Modal.Title>
            Choose Your Account Type
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          <div className="row row-cols-1 row-cols-md-4 g-3">
            {accountTypes.map((item) => (
              <div
                className="col"
                key={item.title}
              >
                <div className="lop-role-card">
                  <div
                    className="lop-role-icon"
                    style={{
                      background: item.bg,
                      color: item.color,
                    }}
                  >
                    {item.icon}
                  </div>

                  <h5 className="lop-role-title">
                    {item.title}
                  </h5>

                  <p className="lop-role-description">
                    {item.desc}
                  </p>

                  {item.knowMore && (
                    <p
                      className="lop-know-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowModal(true);
                        setSelectedType(item.type);
                      }}
                    >
                      Click here to know more
                    </p>
                  )}

                  <div className="lop-role-buttons">
                    <button
                      type="button"
                      className="lop-register"
                      onClick={() =>
                        handleTypeClick(
                          item.type,
                          "register"
                        )
                      }
                    >
                      Register
                    </button>

                    {item.type !== "investor" && (
                      <button
                        type="button"
                        className="lop-login"
                        onClick={() =>
                          handleTypeClick(
                            item.type,
                            "login"
                          )
                        }
                      >
                        Login
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* COMING SOON */}

            {comingSoonTypes.map((item) => (
              <div
                className="col"
                key={item.label}
              >
                <div className="lop-coming-card">
                  <div className="lop-coming-icon">
                    <FaLock />
                  </div>

                  <div className="lop-coming-title">
                    {item.icon}

                    <span>{item.label}</span>
                  </div>

                  <span className="lop-coming-badge">
                    <FaClock size={11} />
                    Coming Soon
                  </span>

                  <p>
                    This account type will be available
                    soon. Stay tuned!
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="lop-modal-close"
            onClick={closeAccountModal}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* TOAST */}

      <ToastContainer />

      {/* ROLE INFORMATION MODAL */}

      <RoleInfoModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        role={selectedType}
      />

      {/* =====================================================
          CSS
      ===================================================== */}

      <style>
        {`

/* =========================================================
   HEADER
========================================================= */

.lop-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  background: #ffffff;
  border-bottom: 1px solid #e7eee9;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.lop-header-inner {
  width: 100%;
  min-height: 72px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;
  padding: 8px 28px;
}

/* =========================================================
   LOGO
========================================================= */

.lop-logo-area {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.lop-logo-wrapper {
  display: flex;
  align-items: center;
}

.lop-logo {
  height: 48px;
  width: auto;
  object-fit: contain;
  cursor: pointer;
}

/* =========================================================
   NAVBAR
========================================================= */

.lop-nav-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
}

.lop-nav {
  display: flex;
  align-items: center;
  gap: 2px;
}

.lop-nav-item {
  position: relative;

  border: none;
  background: transparent;

  color: #374151;

  padding: 9px 11px;

  border-radius: 7px;

  font-size: 14px;
  font-weight: 500;

  white-space: nowrap;

  cursor: pointer;

  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.lop-nav-item:hover {
  color: #35a93e;
  background: #f1faf3;
}

.lop-nav-active {
  color: #35a93e;
  background: #eef9f0;
  font-weight: 600;
}

.lop-nav-active::after {
  content: "";

  position: absolute;

  left: 18%;
  right: 18%;
  bottom: 2px;

  height: 2px;

  border-radius: 2px;

  background: #35a93e;
}

/* =========================================================
   ACCOUNT AREA
========================================================= */

.lop-account-area {
  display: flex;
  align-items: center;
  gap: 5px;

  flex-shrink: 0;
}

.lop-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 7px 8px;

  color: #374151;
  text-decoration: none;

  border-radius: 7px;

  font-size: 14px;

  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.lop-action:hover {
  color: #35a93e;
  background: #f1faf3;
  text-decoration: none;
}

.lop-action-icon {
  font-size: 15px;
}

/* =========================================================
   ACCOUNT BUTTON
========================================================= */

.lop-account-button {
  border: none;

  padding: 9px 18px;

  border-radius: 7px;

  color: #ffffff;
  background: #35a93e;

  font-size: 14px;
  font-weight: 500;

  cursor: pointer;

  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.lop-account-button:hover {
  background: #2b9134;
  transform: translateY(-1px);
}

/* =========================================================
   LOGOUT
========================================================= */

.lop-logout {
  border: 1px solid #35a93e;

  color: #35a93e;
  background: #ffffff;

  padding: 7px 13px;

  border-radius: 7px;

  font-size: 13px;

  cursor: pointer;

  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.lop-logout:hover {
  color: #ffffff;
  background: #35a93e;
}

/* =========================================================
   MOBILE BUTTON
========================================================= */

.lop-mobile-button {
  display: none;

  border: 1px solid #dce9df;

  background: #ffffff;

  color: #35a93e;

  border-radius: 7px;

  padding: 7px;

  cursor: pointer;
}

.lop-mobile-button:hover {
  background: #f1faf3;
}

/* =========================================================
   FILTER OVERLAY
========================================================= */

.lop-filter-overlay {
  position: fixed;
  inset: 0;

  background: rgba(0, 0, 0, 0.45);

  z-index: 1040;

  opacity: 0;
  pointer-events: none;

  transition: opacity 0.25s ease;
}

.lop-filter-overlay-show {
  opacity: 1;
  pointer-events: auto;
}

/* =========================================================
   FILTER SIDEBAR
========================================================= */

.lop-filter-sidebar {
  position: fixed;

  top: 0;
  left: 0;

  width: 320px;
  height: 100vh;

  background: #ffffff;

  z-index: 1050;

  box-shadow: 5px 0 20px rgba(0, 0, 0, 0.12);

  transform: translateX(-100%);

  transition: transform 0.3s ease;

  overflow-y: auto;
}

.lop-filter-sidebar-open {
  transform: translateX(0);
}

.lop-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 16px 18px;

  background: #16351d;
  color: #ffffff;
}

.lop-filter-header h2 {
  margin: 0;
  font-size: 18px;
}

.lop-filter-header button {
  border: none;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
}

.lop-filter-content {
  padding: 18px;
}

.lop-category {
  margin-bottom: 12px;
}

.lop-category-title {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 10px 4px;

  color: #374151;

  font-size: 14px;
  font-weight: 500;

  cursor: pointer;

  border-bottom: 1px solid #edf1ee;
}

.lop-category-title:hover {
  color: #35a93e;
}

.lop-subcategories {
  padding-left: 12px;
}

.lop-subcategory {
  padding: 8px 4px;

  color: #6b7280;

  font-size: 13px;

  cursor: pointer;
}

.lop-subcategory:hover {
  color: #35a93e;
}

.lop-no-category {
  color: #6b7280;
  font-size: 14px;
}

/* =========================================================
   MOBILE DRAWER
========================================================= */

.lop-mobile-drawer {
  position: fixed;

  top: 0;
  right: 0;

  width: min(82vw, 340px);
  height: 100vh;

  z-index: 2000;

  background: #ffffff;

  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.12);

  transform: translateX(105%);

  transition: transform 0.3s ease;

  overflow-y: auto;
}

.lop-mobile-open {
  transform: translateX(0);
}

.lop-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 18px 20px;

  border-bottom: 1px solid #e8eee9;
}

.lop-mobile-title {
  color: #35a93e;

  font-size: 20px;
  font-weight: 600;
}

.lop-mobile-close {
  border: none;
  background: transparent;

  color: #35a93e;

  cursor: pointer;
}

.lop-mobile-content {
  display: flex;
  flex-direction: column;

  padding: 18px;

  gap: 5px;
}

.lop-mobile-nav {
  border: none;
  background: transparent;

  color: #374151;

  text-align: left;

  padding: 11px 12px;

  border-radius: 7px;

  font-size: 14px;

  cursor: pointer;

  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.lop-mobile-nav:hover {
  color: #35a93e;
  background: #f1faf3;
}

.lop-mobile-active {
  color: #35a93e;
  background: #eef9f0;
  font-weight: 600;
}

.lop-mobile-divider {
  height: 1px;

  margin: 10px 0;

  background: #e5ebe6;
}

.lop-mobile-action {
  padding: 11px 12px;

  border-radius: 7px;

  color: #374151;

  text-decoration: none;

  font-size: 14px;
}

.lop-mobile-action:hover {
  color: #35a93e;
  background: #f1faf3;
  text-decoration: none;
}

.lop-mobile-account {
  border: none;

  padding: 11px;

  border-radius: 7px;

  color: #ffffff;
  background: #35a93e;

  font-size: 14px;

  cursor: pointer;
}

.lop-mobile-account:hover {
  background: #2b9134;
}

.lop-mobile-logout {
  border: none;

  background: transparent;

  color: #374151;

  text-align: left;

  padding: 11px 12px;

  border-radius: 7px;

  cursor: pointer;

  font-size: 14px;
}

.lop-mobile-logout:hover {
  color: #35a93e;
  background: #f1faf3;
}

/* =========================================================
   ACCOUNT MODAL
========================================================= */

.modal-content {
  border: none !important;

  border-radius: 14px !important;

  overflow: hidden;

  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.15);
}

.lop-modal-header {
  background: #ffffff;

  border-bottom: 1px solid #e8eee9 !important;
}

.lop-modal-header .modal-title {
  color: #1f2937;

  font-size: 20px;
  font-weight: 600;
}

/* =========================================================
   ROLE CARD
========================================================= */

.lop-role-card {
  height: 100%;
  min-height: 210px;

  display: flex;
  flex-direction: column;

  padding: 18px;

  border: 1px solid #e3e9e5;

  border-radius: 12px;

  background: #ffffff;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.lop-role-card:hover {
  border-color: #35a93e;

  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08);
}

.lop-role-icon {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  margin-bottom: 12px;

  font-size: 18px;
}

.lop-role-title {
  margin-bottom: 8px;

  color: #1f2937;

  font-size: 16px;
  font-weight: 600;
}

.lop-role-description {
  margin-bottom: 8px;

  color: #6b7280;

  font-size: 13px;

  line-height: 1.5;
}

.lop-know-more {
  margin-bottom: 12px;

  color: #0d6efd;

  font-size: 12px;

  text-decoration: underline;

  cursor: pointer;
}

.lop-know-more:hover {
  color: #35a93e;
}

/* =========================================================
   ROLE BUTTONS
   SAME LINE / SAME BOTTOM POSITION
========================================================= */

.lop-role-buttons {
  display: flex;
  align-items: center;

  gap: 8px;

  margin-top: auto;

  min-height: 32px;
}

.lop-register,
.lop-login {
  width: 80px;
  height: 32px;

  flex-shrink: 0;

  border-radius: 6px;

  font-size: 13px;

  cursor: pointer;

  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.lop-register {
  border: 1px solid #35a93e;

  color: #ffffff;

  background: #35a93e;
}

.lop-register:hover {
  background: #2b9134;
}

.lop-login {
  border: 1px solid #35a93e;

  color: #35a93e;

  background: #ffffff;
}

.lop-login:hover {
  color: #ffffff;

  background: #35a93e;
}

/* =========================================================
   COMING SOON
========================================================= */

.lop-coming-card {
  min-height: 210px;
  height: 100%;

  padding: 18px;

  border: 1px solid #e3e7e4;

  border-radius: 12px;

  background: #fafafa;
}

.lop-coming-icon {
  color: #9ca3af;

  margin-bottom: 10px;
}

.lop-coming-title {
  display: flex;
  align-items: center;

  gap: 8px;

  color: #374151;

  font-weight: 600;
}

.lop-coming-badge {
  display: inline-flex;
  align-items: center;

  gap: 4px;

  margin-top: 12px;

  padding: 5px 8px;

  border-radius: 20px;

  background: #fff3cd;

  color: #664d03;

  font-size: 11px;
}

.lop-coming-card p {
  color: #6b7280;

  font-size: 13px;

  margin-top: 16px;
}

/* =========================================================
   MODAL CLOSE
========================================================= */

.lop-modal-close {
  border: 1px solid #6c757d;

  color: #ffffff;

  background: #6c757d;

  border-radius: 6px;

  padding: 7px 16px;

  font-size: 13px;

  cursor: pointer;
}

.lop-modal-close:hover {
  background: #5c636a;
}

/* =========================================================
   INPUT
========================================================= */

.modern-input {
  border: 1px solid #e5e7eb;

  border-radius: 7px;

  padding: 12px 16px;

  background: #ffffff;

  outline: none;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.modern-input:hover {
  border-color: #b8c0ba;
}

.modern-input:focus {
  border-color: #35a93e;

  box-shadow: 0 0 0 3px rgba(53, 169, 62, 0.12);
}

.modern-input:disabled,
.modern-input[readonly] {
  background: #f3f4f6;

  cursor: not-allowed;
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1200px) {

  .lop-header-inner {
    padding-left: 18px;
    padding-right: 18px;
    gap: 10px;
  }

  .lop-nav-item {
    padding-left: 8px;
    padding-right: 8px;

    font-size: 13px;
  }

}

@media (max-width: 1000px) {

  .lop-nav-item {
    padding-left: 6px;
    padding-right: 6px;

    font-size: 12px;
  }

  .lop-action {
    font-size: 12px;
  }

}

@media (max-width: 900px) {

  .lop-nav-wrapper,
  .lop-account-area {
    display: none;
  }

  .lop-mobile-button {
    display: flex;

    align-items: center;
    justify-content: center;
  }

  .lop-header-inner {
    min-height: 65px;
  }

  .lop-logo {
    height: 44px;
  }

}

@media (min-width: 901px) {

  .lop-mobile-drawer {
    display: none;
  }

}

@media (max-width: 767px) {

  .modal-dialog {
    margin: 10px;
  }

  .modal-body {
    padding: 15px !important;
  }

  .lop-role-card {
    min-height: auto;
    height: auto;
  }

  .lop-filter-sidebar {
    width: 290px;
  }

}

/* =========================================================
   EXTRA MODAL RESPONSIVE FIX
========================================================= */

@media (min-width: 768px) {

  .lop-role-card {
    height: 100%;
  }

}

`}
      </style>
    </>
  );
};

export default Header;

